import Alpine from 'alpinejs';
import { getCsrfToken } from '../../utils/helpers.js';

Alpine.store('wishlistProfile', {
    activeMenu: null,
    isDeleting: false,
    isEditing: false,
    selectedIds: [],
    removedIds: [], // Track removed items for DOM removal without page refresh
    totalListings: 0, // Server-rendered initial count, set via x-init

    get activeListingCount() {
        return this.totalListings - this.removedIds.length;
    },

    get activeListingLabel() {
        const n = this.activeListingCount;
        return `${n} saved ${n === 1 ? 'property' : 'properties'}`;
    },
    deletedWishlistUids: [], // Track deleted wishlists for DOM removal without page refresh

    // Modal State
    showConfirmModal: false,
    confirmMessage: '',
    onConfirm: null, // Callback function

    askConfirmation(message, callback) {
        this.confirmMessage = message;
        this.onConfirm = callback;
        this.showConfirmModal = true;
    },

    closeConfirmModal() {
        this.showConfirmModal = false;
        this.confirmMessage = '';
        this.onConfirm = null;
    },

    confirmAction() {
        if (this.onConfirm) {
            this.onConfirm();
        }
        this.closeConfirmModal();
    },

    tempTitle: '',
    originalTitle: '',
    currentTitle: '', // The displayed title (updated after rename without page refresh)

    toggleEditMode(currentTitle = '') {
        this.isEditing = !this.isEditing;
        this.selectedIds = [];
        if (this.isEditing) {
            this.tempTitle = currentTitle;
            this.originalTitle = currentTitle;
        } else {
            this.tempTitle = '';
            this.originalTitle = '';
        }
    },

    cancelEdit() {
        this.isEditing = false;
        this.selectedIds = [];
        this.tempTitle = '';
        this.originalTitle = '';
    },

    toggleSelection(id) {
        // Ensure ID is treated consistently (string/int)
        const strId = String(id);
        if (this.selectedIds.includes(strId)) {
            this.selectedIds = this.selectedIds.filter(i => i !== strId);
        } else {
            this.selectedIds.push(strId);
        }
    },

    async removeSelectedItems(wishlistUid) {
        if (this.selectedIds.length === 0) return;

        this.askConfirmation(
            `Remove ${this.selectedIds.length} items from this collection?`,
            () => this.performRemoveItems(wishlistUid, this.selectedIds)
        );
    },

    async performRemoveItems(wishlistUid, listingIds) {
        this.isDeleting = true;

        try {
            const csrf = await getCsrfToken();
            if (!csrf) { console.error('CSRF token not available'); return; }

            // We need to pass listingIds as array. FormData handles arrays by appending multiple times with []
            const formData = new FormData();
            formData.append('uid', wishlistUid);
            listingIds.forEach(id => formData.append('listingIds[]', id));
            formData.append(csrf.name, csrf.value);

            const response = await fetch('/actions/avari/wishlist/remove-items', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const data = await response.json();

            if (data.success) {
                // Add removed IDs to track them for DOM removal
                listingIds.forEach(id => {
                    if (!this.removedIds.includes(String(id))) {
                        this.removedIds.push(String(id));
                    }
                });
            } else {
                alert('Failed to remove items: ' + (data.message || 'Unknown error'));
            }
        } catch (e) {
            console.error('Error removing items:', e);
            alert('An error occurred while updating the wishlist.');
        } finally {
            this.isDeleting = false;
            this.isEditing = false;
            this.selectedIds = [];
        }
    },

    async removeSingleItem(wishlistUid, listingId) {
        this.askConfirmation(
            'Remove this item from the collection?',
            () => this.performRemoveItems(wishlistUid, [listingId])
        );
    },

    async saveRename(wishlistUid) {
        if (!this.tempTitle || this.tempTitle.trim() === '') {
            alert('Please enter a title.');
            return;
        }

        if (this.tempTitle === this.originalTitle) {
            this.isEditing = false;
            return;
        }

        this.isDeleting = true; // Use isDeleting for loading state for now

        try {
            const csrf = await getCsrfToken();
            if (!csrf) { console.error('CSRF token not available'); return; }

            const formData = new FormData();
            formData.append('uid', wishlistUid);
            formData.append('title', this.tempTitle);
            formData.append(csrf.name, csrf.value);

            const response = await fetch('/actions/avari/wishlist/rename', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const data = await response.json();

            if (data.success) {
                // Update the displayed title without page refresh
                this.currentTitle = this.tempTitle;
                this.originalTitle = this.tempTitle;
                this.isEditing = false;
            } else {
                alert('Failed to rename wishlist: ' + (data.message || 'Unknown error'));
            }
        } catch (e) {
            console.error('Error renaming wishlist:', e);
            alert('An error occurred while renaming the wishlist.');
        } finally {
            this.isDeleting = false;
        }
    },

    toggleMenu(uid) {
        this.activeMenu = this.activeMenu === uid ? null : uid;
    },

    closeMenu() {
        this.activeMenu = null;
    },

    async deleteWishlist(uid) {
        this.askConfirmation(
            'Are you sure you want to delete this collection?',
            () => this.performDeleteWishlist(uid)
        );
    },

    async performDeleteWishlist(uid) {
        this.isDeleting = true;
        this.closeMenu();

        try {
            const csrf = await getCsrfToken();
            if (!csrf) { console.error('CSRF token not available'); return; }

            const formData = new FormData();
            formData.append('uid', uid);
            formData.append(csrf.name, csrf.value);

            const response = await fetch('/actions/avari/wishlist/delete', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const data = await response.json();

            if (data.success) {
                // Add deleted UID to track for DOM removal
                if (!this.deletedWishlistUids.includes(uid)) {
                    this.deletedWishlistUids.push(uid);
                }
            } else {
                alert('Failed to delete wishlist: ' + (data.message || 'Unknown error'));
            }
        } catch (e) {
            console.error('Error deleting wishlist:', e);
            alert('An error occurred while deleting the wishlist.');
        } finally {
            this.isDeleting = false;
        }
    },

    openShareModal(uid) {
        this.closeMenu();
        Alpine.store('share')?.openWishlist(uid);
    }
});
